<div class="px-4 mx-auto max-w-screen-2xl lg:px-8 py-6 space-y-6">

  <!-- Encabezado -->
  <div>
    <h1 class="text-2xl font-bold text-gray-900">
      <i class="fas fa-file-import text-blue-600 mr-2"></i> Registro de cobros desde archivo
    </h1>
    <p class="text-sm text-gray-500 mt-1">
      Carga el Excel de cobros de un lote de domiciliación y registra los pagos de forma controlada.
      El monto, tasa y cuenta se toman del <b>lote</b>. Es idempotente: nunca registra un pago repetido.
    </p>
  </div>

  <!-- Paso 1: Lote -->
  <div class="bg-white shadow-md rounded-lg p-5">
    <div class="flex items-center gap-2 mb-3">
      <span class="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">1</span>
      <h3 class="text-sm font-semibold text-gray-800">Lote a procesar</h3>
    </div>
    <div class="flex items-end gap-3 flex-wrap">
      <div class="flex-1 min-w-[220px] max-w-xs">
        <label class="block text-xs font-medium text-gray-600 mb-1">N° de lote</label>
        <input type="text" [(ngModel)]="lote" (ngModelChange)="onLoteChange()" placeholder="Ej: 00005327"
          (keyup.enter)="consultarLote()"
          class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500">
      </div>
      <button (click)="consultarLote()" [disabled]="consulting || !lote"
        class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2 rounded-lg">
        <i class="fas" [ngClass]="consulting ? 'fa-spinner fa-spin' : 'fa-search'"></i>
        {{ consulting ? 'Consultando…' : 'Consultar lote' }}
      </button>
      <span *ngIf="loteOk" class="text-green-600 text-sm font-medium pb-2"><i class="fas fa-check-circle mr-1"></i> Lote válido</span>
    </div>
  </div>

  <!-- Error -->
  <div *ngIf="errorMsg" class="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
    <i class="fas fa-exclamation-triangle mr-2"></i>{{ errorMsg }}
  </div>

  <!-- Paso 2: Datos del lote -->
  <div *ngIf="loteOk && loteInfo" class="bg-white shadow-md rounded-lg p-5">
    <div class="flex items-center gap-2 mb-3">
      <span class="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">2</span>
      <h3 class="text-sm font-semibold text-gray-800">Datos del lote</h3>
    </div>
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><p class="text-xs text-gray-500">Lote</p><p class="font-semibold text-gray-800">{{ loteInfo.number }}</p></div>
      <div><p class="text-xs text-gray-500">Fecha</p><p class="font-semibold text-gray-800">{{ loteInfo.date | date:'dd/MM/yyyy' }}</p></div>
      <div><p class="text-xs text-gray-500">Tasa</p><p class="font-semibold text-gray-800">{{ loteInfo.rate }} Bs/$</p></div>
      <div><p class="text-xs text-gray-500">Producto</p><p class="font-semibold text-gray-800">{{ loteInfo.product_type }}</p></div>
      <div><p class="text-xs text-gray-500">Cuenta BDV</p><p class="font-semibold text-gray-800">{{ loteInfo.account_number }}</p></div>
      <div><p class="text-xs text-gray-500">Estado lote</p><p class="font-semibold text-gray-800">{{ loteInfo.status }}</p></div>
      <div><p class="text-xs text-gray-500">Cobros del lote</p><p class="font-semibold text-gray-800">{{ loteInfo.total_detalles | number }} <span class="text-gray-400 font-normal">({{ loteInfo.pendientes | number }} pendientes)</span></p></div>
      <div><p class="text-xs text-gray-500">Ya registrados</p><p class="font-semibold text-gray-800">{{ loteInfo.ya_pagados | number }}</p></div>
    </div>
  </div>

  <!-- Paso 3: Archivo -->
  <div *ngIf="loteOk" class="bg-white shadow-md rounded-lg p-5">
    <div class="flex items-center gap-2 mb-3">
      <span class="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold">3</span>
      <h3 class="text-sm font-semibold text-gray-800">Archivo de cobros (.xlsx)</h3>
    </div>
    <label class="dropzone flex items-center justify-center gap-3 px-4 py-5 cursor-pointer text-sm text-gray-500"
      [class.loaded]="fileName">
      <i class="fas fa-file-excel text-green-600 text-lg"></i>
      <span *ngIf="!fileName">Haz clic para subir el Excel de cobros efectivos del lote</span>
      <span *ngIf="fileName" class="text-gray-700 font-medium">{{ fileName }} · {{ rows.length }} cobros</span>
      <input type="file" class="hidden" accept=".xlsx" (change)="onFileSelected($event)">
    </label>
    <div *ngIf="loading" class="mt-3 text-sm text-blue-600"><i class="fas fa-spinner fa-spin mr-2"></i>Analizando archivo…</div>
  </div>

  <!-- Resumen + tabla -->
  <div *ngIf="resumen">
    <!-- Tarjetas -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <div class="bg-white shadow rounded-lg p-3"><p class="text-xs text-gray-500">A registrar</p><p class="text-xl font-bold text-blue-600">{{ resumen.aRegistrar | number }}</p></div>
      <div class="bg-white shadow rounded-lg p-3"><p class="text-xs text-gray-500">Aplicados</p><p class="text-xl font-bold text-green-600">{{ resumen.aplicados || 0 | number }}</p></div>
      <div class="bg-white shadow rounded-lg p-3"><p class="text-xs text-gray-500">Saltados</p><p class="text-xl font-bold text-gray-600">{{ resumen.saltados | number }}</p></div>
      <div class="bg-white shadow rounded-lg p-3"><p class="text-xs text-gray-500">Anomalías</p><p class="text-xl font-bold text-amber-600">{{ resumen.anomalias | number }}</p></div>
      <div class="bg-white shadow rounded-lg p-3"><p class="text-xs text-gray-500">Fallidos</p><p class="text-xl font-bold text-red-600">{{ resumen.fallidos || 0 | number }}</p></div>
    </div>

    <!-- Barra de acciones -->
    <div class="mt-4 flex items-center justify-between gap-3 flex-wrap">
      <div class="relative">
        <input type="text" [(ngModel)]="searchText" placeholder="Buscar cliente, cédula, referencia, crédito…"
          class="w-80 max-w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500">
        <i class="fas fa-search absolute left-3 top-3 text-gray-400 text-sm"></i>
      </div>
      <div class="flex items-center gap-2 flex-wrap">
        <ng-container *ngIf="!registering">
          <button (click)="registrarSiguientes(10)" [disabled]="!pendientes.length"
            class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <i class="fas fa-play"></i> Siguientes 10
          </button>
          <button (click)="registrarTodos()" [disabled]="!pendientes.length"
            class="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <i class="fas fa-forward"></i> Registrar todos ({{ pendientes.length }})
          </button>
        </ng-container>
        <button *ngIf="registering" (click)="detener()"
          class="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <i class="fas fa-stop"></i> Detener
        </button>
        <button (click)="descargarExcel()"
          class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
          <i class="fas fa-file-excel"></i> Descargar
        </button>
      </div>
    </div>

    <!-- Progreso -->
    <div *ngIf="registering" class="mt-3">
      <div class="flex justify-between text-xs text-gray-500 mb-1">
        <span>Registrando…</span><span>{{ progress.done }} / {{ progress.total }}</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div class="bg-blue-600 h-2 rounded-full transition-all" [style.width.%]="progress.total ? (progress.done / progress.total * 100) : 0"></div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mt-3 bg-white shadow-md rounded-lg overflow-hidden">
      <div class="table-wrap">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 sticky top-0 z-10">
            <tr class="text-left text-gray-600">
              <th class="px-3 py-2 font-medium">Estado</th>
              <th class="px-3 py-2 font-medium">Cliente</th>
              <th class="px-3 py-2 font-medium">Cédula</th>
              <th class="px-3 py-2 font-medium">Referencia</th>
              <th class="px-3 py-2 font-medium">Crédito</th>
              <th class="px-3 py-2 font-medium text-center">Cuota</th>
              <th class="px-3 py-2 font-medium text-right">Monto Bs</th>
              <th class="px-3 py-2 font-medium text-right">Monto USD</th>
              <th class="px-3 py-2 font-medium">Detalle</th>
              <th class="px-3 py-2 font-medium text-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let i of filteredItems; trackBy: trackByRef" class="border-t border-gray-100 hover:bg-gray-50">
              <td class="px-3 py-2">
                <span class="text-xs font-medium px-2 py-0.5 rounded-full" [ngClass]="estadoClass(i.estado)">{{ estadoLabel(i.estado) }}</span>
              </td>
              <td class="px-3 py-2 text-gray-800">{{ i.cliente || '—' }}</td>
              <td class="px-3 py-2 text-gray-600">{{ i.cedula || '—' }}</td>
              <td class="px-3 py-2 text-gray-600">{{ i.reference }}</td>
              <td class="px-3 py-2 text-gray-600">{{ i.credito || '—' }}</td>
              <td class="px-3 py-2 text-center text-gray-600">{{ i.cuota || '—' }}</td>
              <td class="px-3 py-2 text-right text-gray-600">{{ i.montoBs | number:'1.2-2' }}</td>
              <td class="px-3 py-2 text-right font-medium text-gray-800">{{ i.montoUsd | number:'1.2-2' }}</td>
              <td class="px-3 py-2 text-gray-500">
                <span *ngIf="i.paymentCode" class="text-green-700 text-xs">Pago: {{ i.paymentCode }}</span>
                <span *ngIf="!i.paymentCode" class="text-xs">{{ i.motivo }}</span>
              </td>
              <td class="px-3 py-2 text-center">
                <button *ngIf="i.estado === 'A_REGISTRAR'" (click)="registrarUno(i)" [disabled]="registering"
                  class="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-medium px-3 py-1.5 rounded-md">
                  <i class="fas fa-check"></i> Registrar
                </button>
                <span *ngIf="i.estado === 'APLICADO'" class="text-green-600"><i class="fas fa-check-circle"></i></span>
              </td>
            </tr>
            <tr *ngIf="filteredItems.length === 0">
              <td colspan="10" class="px-4 py-6 text-center text-gray-400">Sin resultados.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

</div>
